import { RegisterForm } from '@desquared/wave-components-library';
import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { registerV3 } from '@/api/register';
import { OTPIDStore, RegisterFlowStore } from '@/store/otpid';
import GuestUserStore from '@/store/guestUser';
import User from '@/store/user';
import { useTranslations } from 'next-intl';
import { CountryCodeStore } from '@/store/countryCode';
import CountryCodeAside from '@/components/Aside/CountryCodeAside';
import { CountryCodeSideSheetStore } from '../../store/sideSheet';
import styles from './Register.module.css';
import { CompleteProfileIDStore } from '@/store/completeProfileId';

export default function RegisterPage({ config, logo, type }) {
  const [password, setPassWord] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [mobileErrorText, setMobileErrorText] = useState('');
  const [passwordErrorText, setPasswordErrorText] = useState('');
  const [hasBottomError, setHasBottomError] = useState(false);
  const [bottomErrorText, setBottomErrorText] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const router = useRouter();
  const t = useTranslations('login_register');
  const r = useTranslations('common');
  const a = useTranslations('guest_login');
  const lengthWording = config
    ? `${config?.auth?.password?.format?.min}-${config?.auth?.password?.format?.max} ${t(
        'errors.register.validation.characters'
      )}`
    : t('errors.register.validation.8_100_chars');
  const capitalWording = t('errors.register.validation.capital');
  const specialCharWording = t('errors.register.validation.special');
  const userConfig = config?.fields?.user;

  useEffect(() => {
    const CountryCodeStore$ = CountryCodeStore.subscribe(code => setCountryCode(code));

    return () => {
      CountryCodeStore$.unsubscribe();
    };
  }, []);

  const clearAllErrors = () => {
    setEmailErrorText('');
    setMobileErrorText('');
    setPasswordErrorText('');
    setHasBottomError(false);
  };

  const onEmailChange = e => {
    setEmail(e.currentTarget.value.trim());
    clearAllErrors();
  };

  const onMobileChange = e => {
    setMobile(e.currentTarget.value.trim());
    clearAllErrors();
  };

  const onPassWordChange = e => {
    setPassWord(e.currentTarget.value);
    clearAllErrors();
  };

  const onLoginClick = () => {
    router.push('/login');
  };

  const onCountryCodeClick = () => {
    CountryCodeSideSheetStore.open();
  };

  const onSocialClick = async provider => {
    RegisterFlowStore.update(true);
    window.location.href =
      router.locale === router.defaultLocale
        ? `${process.env.NEXT_PUBLIC_WAVE_HOSTNAME}/api/v3/auth/provider/${provider}/initiate?clientRedirectionUrl=${process.env.NEXT_PUBLIC_HOSTNAME}login&tenant=${process.env.tenant}`
        : `${process.env.NEXT_PUBLIC_WAVE_HOSTNAME}/api/v3/auth/provider/${provider}/initiate?clientRedirectionUrl=${process.env.NEXT_PUBLIC_HOSTNAME}/${router.locale}/login&tenant=${process.env.tenant}`;
  };

  const validateForm = () => {
    let isValid = true;

    setHasBottomError(false);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (hasEmail) {
      if (!email || email.trim() === '') {
        setEmailErrorText(r('checkout.required_field'));
        isValid = false;
      } else if (!emailRegex.test(email)) {
        setEmailErrorText(a('errors.invalid_email'));
        isValid = false;
      } else if (!password || password.trim() === '') {
        setPasswordErrorText(r('checkout.required_field'));
        isValid = false;
      }
    } else {
      if (!mobile || mobile.trim() === '') {
        setMobileErrorText(r('checkout.required_field'));
        isValid = false;
      }
    }

    return isValid;
  };

  const formSubmit = async () => {
    if (!validateForm()) return;

    const newMobile = countryCode[0] + mobile;
    GuestUserStore.clear();

    let body;
    if (!hasEmail) {
      body = {
        mobilePhone: newMobile,
      };
    } else {
      body = {
        email: email,
        password: password,
      };
    }
    const { data, error } = await registerV3(body);
    if (error) {
      if (error.code === 'AUTH0007') {
        setEmailErrorText(t('errors.register.email_exists'));
        setMobileErrorText(t('errors.register.mobile_exists'));
        return;
      } else if (error.code === 'AUTH0008') {
        setPasswordErrorText(error.userMessage);
        return;
      }
      setHasBottomError(true);
      setBottomErrorText(t('errors.login.wrong_email_or_password'));
    } else {
      if (data.status === 'NEED_TO_COMPLETE_PROFILE') {
        RegisterFlowStore.update(true);
        CompleteProfileIDStore.update(data.completeProfileRequestId);
        const params = { pageFrom: 'login' };
        User.update({
          ...data.user,
        });
        router.push(
          {
            pathname: '/account-details',
            query: params,
          },
          undefined,
          { shallow: false }
        );
      } else {
        RegisterFlowStore.update(true);
        OTPIDStore.update({ requestId: data.otpInfo.requestId, email: email, mobilePhone: !hasEmail ? newMobile : '' });
        User.update({
          email: email,
          mobilePhone: !hasEmail ? newMobile : '',
        });
        const params = { pageFrom: 'register' };
        router.push(
          {
            pathname: '/otp',
            query: params,
          },
          undefined,
          { shallow: false }
        );
      }
    }
  };

  let hasFacebook = false;
  let hasGoogle = false;
  let hasApple = false;
  let hasEmail = !config?.auth?.methods?.some(el => el?.method === 'MOBILE_PHONE' && el?.active);
  config?.auth?.providers.map(entry => {
    if (entry.provider === 'FACEBOOK') {
      hasFacebook = entry?.active ?? false;
    } else if (entry.provider === 'GOOGLE') {
      hasGoogle = entry?.active ?? false;
    } else if (entry.provider === 'APPLE') {
      hasApple = entry?.active ?? false;
    }
  });
  const hasExternal = hasFacebook || hasGoogle || hasApple;

  return (
    <>
      <div
        className={cn(
          { [styles.container]: password.length === 0 },
          { [styles.passWordContainer]: password.length > 0 }
        )}
      >
        <RegisterForm
          logo={logo}
          logoRedirect={router.locale === router.defaultLocale ? '/' : `/${router.locale}/`}
          onEmailChange={onEmailChange}
          onMobileChange={onMobileChange}
          onPassWordChange={onPassWordChange}
          emailValue={email}
          mobileValue={mobile}
          passwordValue={password}
          loginRedirect={onLoginClick}
          hasFacebook={hasFacebook}
          hasApple={hasApple}
          hasGoogle={hasGoogle}
          hasEmail={hasEmail}
          emailErrorText={emailErrorText ?? emailErrorText}
          emailHelperTextType={emailErrorText ? 'error' : 'helper'}
          mobileHelperTextType={mobileErrorText ? 'error' : 'helper'}
          mobileErrorText={mobileErrorText ?? mobileErrorText}
          passwordErrorText={passwordErrorText ?? passwordErrorText}
          titleText={t('register_title')}
          passwordHelperTextType={passwordErrorText ? 'error' : 'helper'}
          googleButtonText={t('oauth.register.google')}
          appleButtonText={t('oauth.register.apple')}
          facebookButtonText={t('oauth.register.facebook')}
          connectionText={!hasExternal ? '' : hasEmail ? t('connection.register') : t('connection.register_mobile')}
          emailPlaceHolderText={t('email_placeholder')}
          mobileLabelText={t('mobile_placeholder')}
          passwordPlaceHolderText={t('password_placeholder')}
          connectText={t('connect')}
          accountText={t('account_reminder')}
          continueText={t('continue')}
          onGoogleClick={() => onSocialClick('GOOGLE')}
          onFacebookClick={() => onSocialClick('FACEBOOK')}
          onAppleClick={() => onSocialClick('APPLE')}
          hasBottomError={hasBottomError}
          bottomErrorText={bottomErrorText}
          onSubmitForm={formSubmit}
          passwordConfig={config?.auth?.password?.format}
          lengthWording={lengthWording}
          capitalWording={capitalWording}
          specialCharWording={specialCharWording}
          onCodeClick={onCountryCodeClick}
          selectedCodeIndex={0}
          countryCodeLabel={t('country_code')}
          countries={countryCode}
          classOption={type === 1 || type === 2 ? styles.paddingContainer : styles.noPaddingContainer}
          passwordlLabelText={t('password_label')}
          minLength={userConfig?.email?.minLength}
          maxLength={userConfig?.email?.maxLength}
          minLengthErrorText={r('minimun_character')}
        />
      </div>
      <CountryCodeAside />
    </>
  );
}
